/**
 * №13.1 (§1.2) — the record-action bar. Renders the resolved contextual actions
 * for a record (`list_context_actions`) as one row; every launch carries the
 * context prefilled + LOCKED. There is no per-page prefill wiring — the bar is
 * generic and every page mounts the same component.
 *
 * Dispatch by verb shape:
 *   • synthesised `create_<slug>`  → CreateEntityModal, context field locked.
 *   • first-party verb in VERB_FORMS → VerbLaunchModal (context args locked).
 *   • otherwise (reads like get_matter_trust_balance) → the mounting page can
 *     intercept via `onIntercept` (switch a tab / navigate); un-intercepted, the
 *     verb is simply invoked.
 *
 * Cross-module actions (declared by another module on this context — e.g.
 * commercial-spine actions on a legal `matter`) render dashed with a provenance
 * chip, so it is legible that they light up only because that module is installed.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../../lib/mcp-client";
import { showToast } from "../../components/primitives";
import { useEntityType } from "../../../lib/hooks/use-kuvio";
import { useContextActions, atPlacement, type ResolvedAction, type ActionPlacement } from "../../hooks/use-context-actions";
import { VERB_FORMS, synthesisedCreateSlug } from "./verb-forms";
import { VerbLaunchModal } from "./VerbLaunchModal";
import { CreateEntityModal } from "./CreateEntityModal";
import { completeAction, effectiveOnSuccess } from "./action-completion";

const MODULE_SHORT: Record<string, string> = { "commercial-spine": "spine", legal: "legal" };

function ClockGlyph() {
  return <svg className="g" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
}

export function ContextActionBar({
  context,
  id,
  placement = "record_actions",
  homeModule,
  only,
  onIntercept,
  onDone,
}: {
  context: string;
  id: string;
  placement?: ActionPlacement;
  /** The module that owns this context type — other modules' actions render dashed. */
  homeModule?: string;
  /** Render only these verbs (e.g. a card that hosts one add-action). */
  only?: string[];
  /** Return true to handle an action yourself (tab switch / navigate). */
  onIntercept?: (action: ResolvedAction) => boolean;
  /** Called after any launched verb/create completes (refetch reads). */
  onDone?: (result?: unknown) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const actions = useContextActions(context, id);
  const rows = atPlacement(actions.data?.actions, placement).filter((a) => !only || only.includes(a.verb));

  const [launch, setLaunch] = useState<ResolvedAction | null>(null);
  const [createFor, setCreateFor] = useState<{ action: ResolvedAction; slug: string } | null>(null);
  const createType = useEntityType(createFor?.slug);

  async function run(action: ResolvedAction) {
    if (onIntercept?.(action)) return;
    const createSlug = synthesisedCreateSlug(action.verb);
    if (createSlug) { setCreateFor({ action, slug: createSlug }); return; }
    if (VERB_FORMS[action.verb]) { setLaunch(action); return; }
    // Fallback: a read/idempotent verb with no form and no interceptor — invoke it,
    // then run the completion contract (invalidate + re-resolve + confirm).
    try {
      const r = await mcpClient.toolsCall(action.verb, action.prefill);
      if (r.isError) throw new Error(r.content?.[0]?.text ?? `${action.verb} failed`);
      completeAction({
        verb: action.verb,
        onSuccess: effectiveOnSuccess(action.verb, action.on_success),
        target: null,
        qc, navigate,
        message: `${action.label} done`,
      });
      onDone?.(r.structuredContent);
    } catch (e) {
      showToast((e as Error).message, "error");
    }
  }

  if (rows.length === 0) return null;

  return (
    <div className="actionbar">
      {rows.map((a) => {
        const xmod = homeModule ? a.module !== homeModule : false;
        const isCreate = !!synthesisedCreateSlug(a.verb) || /^create_/.test(a.verb);
        return (
          <button key={`${a.verb}:${a.label}`} className={`act${xmod ? " act--xmod" : ""}`} onClick={() => void run(a)}>
            {a.verb === "record_time" ? <ClockGlyph /> : isCreate ? <span aria-hidden>＋</span> : null}
            {a.label}
            {/* Provenance chip ONLY for cross-module actions (an action another
                module contributes to this context) — so it's legible WHY it's
                here. Same-module actions don't tag themselves (that just jammed
                the module name onto the label, e.g. "Open matterlegal"). */}
            {xmod && <span className="prov">{MODULE_SHORT[a.module] ?? a.module}</span>}
          </button>
        );
      })}

      {launch && VERB_FORMS[launch.verb] && (
        <VerbLaunchModal
          action={launch}
          spec={VERB_FORMS[launch.verb]!}
          open={!!launch}
          onClose={() => setLaunch(null)}
          onDone={(r) => { setLaunch(null); onDone?.(r); }}
        />
      )}

      {createFor && createType.data && (
        <CreateEntityModal
          entityType={createType.data}
          open={!!createFor}
          heading={createFor.action.label}
          lockedPrefill={createFor.action.prefill}
          onSuccess={effectiveOnSuccess(createFor.action.verb, createFor.action.on_success)}
          originVerb={createFor.action.verb}
          onClose={() => setCreateFor(null)}
          onSaved={() => { setCreateFor(null); onDone?.(); }}
        />
      )}
    </div>
  );
}
